/**
 * Hooks that surface the global timezone state into React components.
 *
 * - {@link useTimezone}      → the current IANA tz string. Components
 *   that call it re-render when the resolved zone changes.
 * - {@link useTimezoneInfo}  → {@code {tz, source, userTz, orgTz}} — for
 *   UI that must say WHERE the zone came from ("Detected", "Organization
 *   default", "Your profile").
 * - {@link useTimePreference}→ {@code {tz, locale, timeFormat}} for
 *   components that need the full preference set.
 * - {@link useFormatDate}    → returns a memoised
 *   {@code (value, opts) => string} formatter. Use this in lists /
 *   tables where you'd otherwise call {@code moment(x).format()} in
 *   the render path.
 */
import { useSyncExternalStore, useCallback } from 'react';
import {
  subscribe, getSnapshot,
  getCurrentTz, getTzSource, getUserTz, getOrgDefaultTz,
  getCurrentLocale, getCurrentTimeFormat,
} from './timezoneStore';
import { formatDate as formatDateImpl } from './formatDate';

export function useTimezone() {
  useSyncExternalStore(subscribe, getSnapshot);
  return getCurrentTz();
}

export function useTimezoneInfo() {
  useSyncExternalStore(subscribe, getSnapshot);
  return {
    tz: getCurrentTz(),
    source: getTzSource(),
    userTz: getUserTz(),
    orgTz: getOrgDefaultTz(),
  };
}

export function useTimePreference() {
  useSyncExternalStore(subscribe, getSnapshot);
  return {
    tz: getCurrentTz(),
    locale: getCurrentLocale(),
    timeFormat: getCurrentTimeFormat(),
  };
}

export function useFormatDate() {
  // Subscribe so the component re-renders when tz / locale / format
  // changes. The returned function reads from the store at call time,
  // so it always uses the latest values.
  useSyncExternalStore(subscribe, getSnapshot);
  return useCallback((value, opts) => formatDateImpl(value, opts), []);
}
